










































































































































































































































































import { setTimeout } from 'timers';
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { CampaignStep } from '~/lib/enums/campaign';
import { SaveMessageState } from '../lib/enums/saveMessage';
import { SmsHtmlParser } from '../lib/utilities/smsHtmlParser';

const TemplateStepRouteName = 'campaigns-id-template';
const campaignEngagementName = 'gtm-campaign-engagement-name';
const campaignCsvName = 'gtm-campaign-csv-name';

interface NavigationItem {
  name: CampaignStep;
  description?: String;
  enabled: boolean;
  complete: boolean;
  required: boolean;
  icon: string;
  continueButtonText?: string;
  hide?: boolean;
  continueButtonClass?: string;
}

export default Vue.extend({
  name: 'CampaignNavigation',

  props: {
    newCampaign: {
      type: Boolean,
      default: false
    }
  },

  updated() {
    this.setGtmClass();
  },

  created() {
    this.onRoute(this.$route.name);
  },

  data: function () {
    return {
      onSaveCompleteTimeOut: null as number | null,
      SaveMessageState,
      currentNavItem: null as NavigationItem | null,
      focusOnName: false,
      hoverItem: null,
      showCampaignName: false,
      sendingVerificationEmail: false
    };
  },

  computed: {
    ...mapGetters('client', ['hasMessageSendingPhoneNumber']),
    ...mapState('components/campaign-navigation', [
      'enabled',
      'continueText',
      'secondaryButton',
      'saveMessageState',
      'allowChanges',
      'continueButtonLabel'
    ]),
    ...mapState('campaign', ['name', 'sms', 'classicTemplateId', 'objective']),
    ...mapGetters('campaign', [
      'hasAudience',
      'designStepIsRequired',
      'designStepIsCompleted',
      'designStepStillNeedsToBeCompleted',
      'hasSmsStep',
      'isLinkOnlyCampaign',
      'isSimpleSmsCampaign',
      'isEngagementCampaign',
      'isScheduledCampaign'
    ]),
    hasPhoneNumber() {
      return this.hasMessageSendingPhoneNumber;
    },
    disabledTooltip() {
      if (this.onReviewPage && !this.hasPhoneNumber) {
        return false;
      }

      return this.enabled;
    },
    customContinueButtonLabel() {
      return this.continueButtonLabel;
    },

    campaignName: {
      get(): string {
        return this.name;
      },
      set(value: string) {
        this.setCampaignName({
          name: value
        });
      }
    },
    showCampaignSaveMessage: {
      get(): SaveMessageState {
        return this.saveMessageState;
      },
      set(value: SaveMessageState) {
        this.onSaveCompleteTimeOut && clearTimeout(this.onSaveCompleteTimeOut);
        if (value == SaveMessageState.Saved) {
          this.setSaveCompleteTimeOut();
        }
        this.setSaveMessageState({ saveMessageState: value });
      }
    },

    isPreviewModeOn(): boolean {
      return this.$route.name === 'campaigns-id-design-preview';
    },

    previewModeSelected: {
      get(): boolean {
        return this.isPreviewModeOn;
      },
      set(value: boolean) {
        const routeName = value
          ? 'campaigns-id-design-preview'
          : 'campaigns-id-design';

        this.$router.push({
          name: routeName,
          params: {
            id: this.$route.params.id
          }
        });
      }
    },

    showSecondaryButton(): boolean {
      // Scheduling isn't available yet.
      return (
        this.secondaryButton &&
        this.secondaryButton.toLowerCase() !== 'schedule'
      );
    },

    navigationItems(): NavigationItem[] {
      return [
        {
          name: CampaignStep.Design,
          enabled: this.allowChanges,
          complete: !this.designStepStillNeedsToBeCompleted,
          required: this.designStepIsRequired,
          icon: 'mdi-palette',
          hide: this.isSimpleSmsCampaign,
          continueButtonClass: this.isLinkOnlyCampaign
            ? 'gtm-campaign-csv-design'
            : 'gtm-campaign-engagement-design'
        },
        {
          name: CampaignStep.SMS,
          description: 'Message',
          enabled: this.allowChanges,
          complete: this.smsStepComplete,
          required: true,
          icon: 'mdi-message',
          hide: !this.hasSmsStep,
          continueButtonClass: this.isSimpleSmsCampaign
            ? 'gtm-campaign-standard-sms'
            : 'gtm-campaign-engagement-sms'
        },
        {
          name: CampaignStep.Audience,
          enabled: this.audienceStepEnabled,
          complete: this.hasAudience,
          required: true,
          icon: 'mdi-account-group',
          continueButtonClass: this.isLinkOnlyCampaign
            ? 'gtm-audience-csv-next'
            : 'gtm-audience-all-next'
        },
        {
          name: CampaignStep.Review,
          enabled: this.reviewStepEnabled,
          complete: false,
          icon: this.isLinkOnlyCampaign
            ? 'mdi-file-download-outline'
            : 'mdi-send',
          required: true,
          continueButtonText: this.isLinkOnlyCampaign
            ? 'Generate CSV'
            : this.isScheduledCampaign
            ? 'Done'
            : 'Schedule'
        }
      ];
    },

    visibleNavigationItems(): NavigationItem[] {
      return this.navigationItems.filter(x => !x.hide);
    },

    reviewStepEnabled(): boolean {
      if (this.isScheduledCampaign) return true;

      const passesCommonReviewStepChecks =
        this.allowChanges &&
        !this.designStepStillNeedsToBeCompleted &&
        this.hasPhoneNumber;

      if (this.hasSmsStep) {
        return passesCommonReviewStepChecks && this.smsStepComplete;
      }

      return passesCommonReviewStepChecks;
    },

    audienceStepEnabled(): boolean {
      return (
        (this.hasSmsStep ? this.smsStepComplete : true) && this.allowChanges
      );
    },

    onTemplatePage(): boolean {
      return this.$route.name === TemplateStepRouteName;
    },

    onObjectivePage(): boolean {
      return this.$route.name === 'campaigns-objective';
    },

    onNewPage(): boolean {
      return this.$route.name === 'campaigns-new';
    },

    onDesignPage(): boolean {
      return !!this.$route.name?.includes('-design');
    },

    onReviewPage(): boolean {
      return !!this.$route.name?.includes('review');
    },

    showContinueButton(): boolean {
      return !this.onTemplatePage && !this.onObjectivePage && !this.onNewPage;
    },

    campaignExitText(): string {
      if (this.newCampaign) {
        return 'Cancel';
      }
      return 'Exit';
    },

    smsStepComplete(): boolean {
      if (this.designStepIsRequired) {
        return (
          !!SmsHtmlParser.getCampaignLinkElement(this.sms.text) &&
          !SmsHtmlParser.hasNoSpaceAroundCampaignLinkElement(this.sms.text)
        );
      } else {
        return !!this.sms.text;
      }
    }
  },

  watch: {
    saveMessageState: 'saveStateChanged',
    '$route.name': 'onRoute'
  },

  methods: {
    ...mapActions('user', ['sendVerificationEmail']),
    ...mapMutations('campaign', {
      setCampaignName: 'setName'
    }),
    ...mapMutations('components/campaign-navigation', {
      enableContinuation: 'setEnabled',
      continuationText: 'setContinueText',
      setSaveMessageState: 'setSaveMessageState',
      setAllowChanges: 'setAllowChanges',
      setEnabled: 'setEnabled'
    }),
    ...mapActions('campaign', ['updateCampaign']),

    async navigateToPage(step: CampaignStep, isEnabled: boolean) {
      if (!isEnabled) return;

      const response = await this.updateCampaign({
        id: this.$route.params.id,
        payload: {
          step: step
        }
      });

      if (response.success) {
        if (step === CampaignStep.Design && !this.designStepIsCompleted) {
          this.$router.push({
            name: TemplateStepRouteName
          });
        } else {
          this.$router.push({
            name: `campaigns-id-${step}`
          });
        }
      } else {
        this.$nuxt.$emit('notify', {
          message: this.$t('errors.unexpectedDefaultMessage'),
          type: 'error'
        });
      }
    },

    setGtmClass() {
      this.$refs['nextButton'].$el.getElementsByTagName(
        'span'
      )[0].classList = this.getContinueButtonClass();
    },

    getContinueButtonClass() {
      if (this.newCampaign) {
        return this.isLinkOnlyCampaign
          ? campaignCsvName
          : campaignEngagementName;
      } else {
        return this.currentNavItem.continueButtonClass;
      }
    },

    getNavIconColor(item: NavigationItem): string {
      if (!item.required) return 'primary';
      if (this.isCurrentNavItem(item) || item.complete) return 'white';
      return 'primary';
    },

    isCurrentNavItem(item: NavigationItem): boolean {
      const templateSelectionPage =
        this.$route.name === TemplateStepRouteName && item.name === 'design';
      return (
        this.$route.name?.includes('-' + item.name) || templateSelectionPage
      );
    },

    isNavIconOutlined(item: NavigationItem): boolean {
      if (item.enabled && !item.required) {
        return true;
      }

      return item.enabled && !this.isCurrentNavItem(item) && !item.complete;
    },

    getNavIcon(item: NavigationItem): string {
      // Get icon to display for the nav item based on its state
      if (!item.enabled) return 'mdi-lock';
      if (item.required && item.complete && !this.isCurrentNavItem(item))
        return 'mdi-check';

      // Else return step specific icon
      return item.icon;
    },

    saveName() {
      if (!this.$route.params?.id) return;

      this.updateCampaign({
        id: this.$route.params.id,
        payload: {
          name: this.campaignName
        }
      });
    },

    saveStateChanged() {
      this.showCampaignSaveMessage = this.saveMessageState;

      if (
        this.currentNavItem.name === 'audience' &&
        this.showCampaignSaveMessage != SaveMessageState.None
      ) {
        this.setAllowChanges({
          allowChanges:
            this.showCampaignSaveMessage !== SaveMessageState.Pending
        });
        this.setEnabled({
          enabled: this.showCampaignSaveMessage !== SaveMessageState.Pending
        });
      }
    },

    cancel() {
      this.$router.push('/campaigns');
    },

    secondaryButtonClicked() {
      this.$nuxt.$emit('campaign-navigation-secondary-button');
    },

    // Using 'onContinue' instead of 'continue' as 'continue' is a reserved word in JS.
    onContinue() {
      this.$nuxt.$emit('campaign-navigation-continue');
    },

    setCurrentNavItem(navItem: NavigationItem) {
      this.currentNavItem = navItem;
    },

    setSaveCompleteTimeOut() {
      this.onSaveCompleteTimeOut = setTimeout(() => {
        if (this.saveMessageState == SaveMessageState.Saved)
          this.setSaveMessageState({ saveMessageState: SaveMessageState.None });
      }, 3000);
    },

    onRoute(routeName: string | null | undefined) {
      const currentRouteItem = this.visibleNavigationItems.find(f =>
        routeName?.includes(f.name)
      );

      const designNavItem = this.visibleNavigationItems.find(
        f => f.name === 'design'
      ) as NavigationItem;
      this.setCurrentNavItem(currentRouteItem ?? designNavItem);
    },
    openIntercomChat() {
      this.$intercom.openChat(
        "Hi Mobiz, I'd like to get a verified number, please contact me."
      );
    },
    async sendVerificationEmailToUser(): Promise<void> {
      if (!this.sendingVerificationEmail) {
        this.sendingVerificationEmail = true;
        const response = await this.sendVerificationEmail();
        this.sendingVerificationEmail = false;
        if (response.success) {
          this.$nuxt.$emit('notify', {
            message: 'Verification email sent.',
            type: 'success'
          });
        } else {
          this.$nuxt.$emit('notify', {
            message:
              'Unable to send verification email. Please try again later.',
            type: 'error'
          });
        }
      }
    }
  }
});
